import { useCallback, useEffect, useLayoutEffect, useState } from "react";

// Marca los paneles flotantes para que otros listeners puedan distinguir su scroll
// interno del scroll de la página (ver el efecto de color de la barra superior).
const FLYOUT_PANEL_DATA_ATTRIBUTE = "data-flyout-panel";
export const FLYOUT_PANEL_ATTRIBUTE = { [FLYOUT_PANEL_DATA_ATTRIBUTE]: "true" };
export const FLYOUT_PANEL_SELECTOR = `[${FLYOUT_PANEL_DATA_ATTRIBUTE}]`;

export const FLYOUT_PLACEMENT_BOTTOM = "bottom";
export const FLYOUT_PLACEMENT_RIGHT = "right";

// Margen mínimo que se respeta con los bordes de la ventana
const VIEWPORT_MARGIN = 8;
// Altura mínima que debe quedar visible antes de plantearse abrir hacia el otro lado
const MIN_VISIBLE_HEIGHT = 120;

/**
 * Ancho de la barra de scroll del panel flotante que contiene al elemento. El CSS la
 * oculta, así que normalmente es 0; se contempla por si alguna plataforma la reserva,
 * porque se pinta por encima del flyout y dejaría una franja muerta para el ratón.
 */
const getHostScrollbarWidth = (element) => {
  const scrollHost = element?.closest(FLYOUT_PANEL_SELECTOR);
  return scrollHost ? scrollHost.offsetWidth - scrollHost.clientWidth : 0;
};
// Estado mientras el panel se mide, antes de conocer su posición definitiva
const HIDDEN_STATE = { style: { top: 0, left: 0, visibility: "hidden" }, hasMoreBelow: false };

const clamp = (value, min, max) => Math.max(min, Math.min(value, max));

/**
 * Calcula la posición fija y la altura máxima de un panel flotante (submenú) para que
 * nunca se salga de la ventana. Cuando el contenido no cabe, el panel recorta su altura
 * y el propio panel es el que hace scroll.
 */
const useFlyoutPanel = (anchorRef, panelRef, open, placement = FLYOUT_PLACEMENT_BOTTOM) => {
  const [state, setState] = useState(HIDDEN_STATE);

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current;
    const panel = panelRef.current;
    if (!anchor || !panel) return;

    const anchorRect = anchor.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const panelWidth = panel.offsetWidth;
    // scrollHeight refleja el alto real del contenido aunque el panel ya esté recortado
    const contentHeight = panel.scrollHeight;

    let top;
    let left;

    if (placement === FLYOUT_PLACEMENT_RIGHT) {
      // La barra de scroll del panel padre se pinta por encima del flyout: lo apartamos
      left = anchorRect.right + getHostScrollbarWidth(anchor);
      if (left + panelWidth > viewportWidth - VIEWPORT_MARGIN) {
        // No cabe a la derecha: lo abrimos hacia la izquierda del ancla
        left = Math.max(VIEWPORT_MARGIN, anchorRect.left - panelWidth);
      }
      // Arranca a la altura del item del que se despliega. Si desde ahí no cabe entero,
      // sube solo lo justo para que quepa (nunca más), y si ni con toda la ventana cabe,
      // se apoya en el margen superior y es el propio panel el que hace scroll.
      const panelHeight = Math.min(contentHeight, viewportHeight - 2 * VIEWPORT_MARGIN);
      top = clamp(anchorRect.top, VIEWPORT_MARGIN, viewportHeight - VIEWPORT_MARGIN - panelHeight);
    } else {
      left = anchorRect.left;
      if (left + panelWidth > viewportWidth - VIEWPORT_MARGIN) {
        left = Math.max(VIEWPORT_MARGIN, viewportWidth - VIEWPORT_MARGIN - panelWidth);
      }
      const spaceBelow = viewportHeight - anchorRect.bottom - VIEWPORT_MARGIN;
      const spaceAbove = anchorRect.top - VIEWPORT_MARGIN;
      if (spaceBelow < MIN_VISIBLE_HEIGHT && spaceAbove > spaceBelow) {
        // Apenas hay hueco debajo del ancla: desplegamos hacia arriba
        top = Math.max(VIEWPORT_MARGIN, anchorRect.top - Math.min(contentHeight, spaceAbove));
      } else {
        top = anchorRect.bottom;
      }
    }

    const maxHeight = Math.floor(viewportHeight - top - VIEWPORT_MARGIN);
    setState({
      // Sin redondear: el ancla suele caer en una posición fraccionaria y redondear deja
      // un escalón de 1px entre el item y el panel que cuelga de él
      style: { top, left, maxHeight },
      // Se calcula aquí porque updatePosition ya se dispara al abrir, al redimensionar
      // y en cada scroll, que es justo cuando esto puede cambiar
      hasMoreBelow: contentHeight - panel.scrollTop > maxHeight + 1,
    });
  }, [anchorRef, panelRef, placement]);

  useLayoutEffect(() => {
    if (!open) {
      setState(HIDDEN_STATE);
      return;
    }
    updatePosition();
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return undefined;
    const handleViewportChange = () => updatePosition();
    window.addEventListener("resize", handleViewportChange);
    // En captura para enterarnos también del scroll del panel padre
    window.addEventListener("scroll", handleViewportChange, true);
    return () => {
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
    };
  }, [open, updatePosition]);

  return state;
};

export default useFlyoutPanel;
